/**
 * Слой стен и проёмов.
 * Управляет графом узлов, стен и проёмов. Видимость слоя тоже здесь.
 */
import { create } from "zustand";
import {
	OPENING_LIMITS,
	OPENING_PRESET,
	clampOffset,
	getEmptyPlanLayers,
	reconcilePlanStages,
	uid,
	wallLength,
	type Opening,
	type OpeningKind,
	type Wall,
	type WallGraph,
} from "@/shared/plan";
import { dist } from "@/shared/lib/geometry";
import type { OpeningPreset } from "../projectStore/projectStore";

interface WallsLayerState {
	items: WallGraph;
	visible: boolean;

	moveNode: (id: string, x: number, y: number) => void;
	updateWall: (
		id: string,
		patch: Partial<
			Pick<Wall, "thickness" | "height" | "material" | "bearing" | "demolished" | "mounted">
		>,
	) => void;
	setWallLength: (id: string, length: number, anchor?: "a" | "b") => void;
	addOpening: (
		wallId: string,
		kind: OpeningKind,
		offset: number,
		preset?: OpeningPreset,
	) => string | null;
	updateOpening: (id: string, patch: Partial<Omit<Opening, "id" | "wallId">>) => void;
	setOpeningOffset: (id: string, offset: number) => void;

	setVisible: (visible: boolean) => void;
	toggleVisible: () => void;

	// Private methods for undo/redo
	restoreWalls: (walls: WallGraph) => void;
}

export const useWallsStore = create<WallsLayerState>((set, get) => ({
	items: getEmptyPlanLayers().walls,
	visible: true,

	moveNode: (id, x, y) =>
		set((s) => {
			const graph = s.items;
			const node = graph.nodes[id];
			if (!node) return s;
			return {
				items: reconcilePlanStages({
					...graph,
					nodes: { ...graph.nodes, [id]: { ...node, x, y } },
				}),
			};
		}),

	updateWall: (id, patch) =>
		set((s) => {
			const graph = s.items;
			const wall = graph.walls[id];
			if (!wall) return s;
			const next: Wall = { ...wall, ...patch };
			const length = wallLength(graph, next);
			const openings = { ...graph.openings };

			// Проёмы не должны вылезать за габариты изменившейся стены
			for (const o of Object.values(openings)) {
				if (o.wallId !== id) continue;
				const width = Math.min(o.width, Math.max(OPENING_LIMITS.minWidth, length - 100));
				const sill = Math.min(o.sill, Math.max(0, next.height - OPENING_LIMITS.minHeight));
				const clamped = clampOffset(o.offset, width, length) ?? o.offset;
				openings[o.id] = {
					...o,
					width,
					sill,
					height: Math.min(o.height, next.height - sill),
					offset: clamped,
				};
			}

			return {
				items: reconcilePlanStages({
					...graph,
					walls: { ...graph.walls, [id]: next },
					openings,
				}),
			};
		}),

	setWallLength: (id, length, anchor = "a") =>
		set((s) => {
			const graph = s.items;
			const wall = graph.walls[id];
			if (!wall) return s;
			const a = graph.nodes[wall.a];
			const b = graph.nodes[wall.b];
			if (!a || !b) return s;

			const current = dist(a, b);
			if (current < 1 || length < 100) return s;

			const fixed = anchor === "a" ? a : b;
			const moving = anchor === "a" ? b : a;
			const direction = {
				x: (moving.x - fixed.x) / current,
				y: (moving.y - fixed.y) / current,
			};
			const next = {
				...moving,
				x: Math.round(fixed.x + direction.x * length),
				y: Math.round(fixed.y + direction.y * length),
			};
			// Двигается только дальний конец этой стены: соседняя стена в этом углу остаётся на
			// месте другим концом и может встать наискось — так каждой стене задают свой размер,
			// а противоположная стена комнаты не меняется
			const nodes = { ...graph.nodes, [moving.id]: next };
			return { items: reconcilePlanStages({ ...graph, nodes }) };
		}),

	addOpening: (wallId: string, kind, offset: number, customPreset?: OpeningPreset) => {
		const current = get();
		const graph = current.items;
		const wall = graph.walls[wallId];
		if (!wall) return null;
		const length = wallLength(graph, wall);
		if (length < 500) return null;

		const preset = customPreset ?? OPENING_PRESET[kind as keyof typeof OPENING_PRESET];
		if (!preset) return null;
		const width = Math.min(preset.width, Math.max(OPENING_LIMITS.minWidth, length - 200));
		const id = uid("o");
		const opening: Opening = {
			id,
			wallId,
			kind,
			offset: clampOffset(offset, width, length) ?? offset,
			width,
			height: Math.min(preset.height, wall.height - preset.sill),
			sill: preset.sill,
			...(preset.item ? { item: preset.item } : {}),
		};
		set({
			items: {
				...graph,
				openings: { ...graph.openings, [id]: opening },
			},
		});
		return id;
	},

	updateOpening: (id, patch) =>
		set((s) => {
			const graph = s.items;
			const o = graph.openings[id];
			if (!o) return s;
			const wall = graph.walls[o.wallId];
			if (!wall) return s;
			const length = wallLength(graph, wall);
			const merged = { ...o, ...patch };
			const width = Math.max(OPENING_LIMITS.minWidth, Math.min(merged.width, length - 100));
			const sill = Math.max(0, Math.min(merged.sill, wall.height - OPENING_LIMITS.minHeight));
			const height = Math.max(
				OPENING_LIMITS.minHeight,
				Math.min(merged.height, wall.height - sill),
			);
			const clamped = clampOffset(merged.offset, width, length) ?? merged.offset;
			const next: Opening = {
				...merged,
				width,
				sill,
				height,
				offset: clamped,
			};
			return {
				items: {
					...graph,
					openings: { ...graph.openings, [id]: next },
				},
			};
		}),

	setOpeningOffset: (id, offset) =>
		set((s) => {
			const graph = s.items;
			const o = graph.openings[id];
			if (!o) return s;
			const wall = graph.walls[o.wallId];
			if (!wall) return s;
			const length = wallLength(graph, wall);
			const clamped = clampOffset(offset, o.width, length) ?? offset;
			return {
				items: {
					...graph,
					openings: {
						...graph.openings,
						[id]: { ...o, offset: clamped },
					},
				},
			};
		}),

	setVisible: (visible) => set({ visible }),
	toggleVisible: () => set((s) => ({ visible: !s.visible })),

	restoreWalls: (walls) => set({ items: walls }),
}));
